import { useCallback, useState } from 'react';
import { mutate } from 'swr';
import { useRouter } from 'next/navigation';
import { useUIStore } from '~/store';
import { useUser } from './useUser';
import { useAgency } from './useAgency';
import { useTracking } from './useTracking';
import { UserAction, NestedHome, NestedProject, NestedSearch } from '@habity/types';
import { hasCompleteProfile } from '@habity/utils';
import { revalidateBuyerDashboard } from '~/app/actions/revalidateBuyerDashboard';
import { executeUserAction as executeUserActionService } from '~/lib/actions/executeUserAction';

interface ExecuteActionParams {
  /** The home to perform the action on */
  home?: NestedHome;
  /** The project to perform the action on */
  project?: NestedProject;
  /** The action to perform */
  action: UserAction;
  /** Optional redirect URL after action */
  redirectTo?: string;
  /** Optional viewing slot ID for booking viewings (used for both home and project viewings) */
  viewingSlotId?: number;
  /** Optional search filters for created_search_profile action */
  searchFilters?: Partial<NestedSearch>;
  /** Skip showing confirmation modal - caller handles confirmation UI (e.g., inline in onboarding) */
  skipConfirmationModal?: boolean;
  /** Seller action data for seller events */
  sellerActionData?: import('@habity/types').SellerActionData;
}

interface ExecuteActionResult {
  success: boolean;
  error?: string;
  /** Whether the user needs to complete login/signup (modal was opened) */
  needsAuth?: boolean;
  /** Full response from the action (for inline confirmation handling) */
  response?: any;
}

interface UseUserActionReturn {
  /** 
   * Execute an action - handles all logic for logged in/out users.
   * For logged-in users with complete profiles: performs action immediately, shows confirmation.
   * For others: opens the login modal at the appropriate step.
   */
  executeAction: (params: ExecuteActionParams) => Promise<ExecuteActionResult>;
  /** Whether any action is currently being processed */
  /** Whether the current user has a complete profile (can execute actions immediately) */
  canExecuteImmediately: boolean;
  isLoading: boolean;
}

/**
 * Centralized hook for handling ALL user actions (buyer and seller) with unified flow.
 * 
 * This is the SINGLE SOURCE OF TRUTH for action execution logic.
 * 
 * For logged-in users with complete profiles:
 * - Performs the action immediately via executeUserAction service
 * - Refreshes user data
 * - Shows the ActionConfirmationModal directly (no login form)
 * 
 * For logged-out users OR logged-in users with incomplete profiles:
 * - Opens the enhanced login modal at the appropriate step
 * - After form completion, action is performed and confirmation shown
 * 
 * Usage:
 * ```tsx
 * const { executeAction, isLoading } = useUserAction();
 * 
 * // In click handler
 * await executeAction({ home, action: 'added_to_wishlist' });
 * ```
 */
export const useUserAction = (): UseUserActionReturn => {
  const { user } = useUser();
  const { agency } = useAgency();
  const { identify } = useTracking();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  // Get store functions only - we'll get modals fresh inside executeAction via getState()
  const setModals = useUIStore((state) => state.setModals);
  const setLoginViaViewingSlotId = useUIStore((state) => state.setLoginViaViewingSlotId);

  const canExecuteImmediately = Boolean(user && hasCompleteProfile(user));

  const executeAction = useCallback(async ({
    home,
    project,
    action,
    redirectTo,
    viewingSlotId,
    searchFilters,
    skipConfirmationModal = false,
    sellerActionData,
  }: ExecuteActionParams): Promise<ExecuteActionResult> => {
    // Store viewing slot ID if provided (used for both home and project viewings)
    if (viewingSlotId) {
      setLoginViaViewingSlotId(viewingSlotId);
    }

    // If user is logged in with complete profile, perform action directly
    if (user && hasCompleteProfile(user)) {
      setIsLoading(true);

      try {
        // Use the centralized service to execute the action
        const result = await executeUserActionService({
          email: user.email,
          agencyId: agency?.id,
          home,
          project,
          action,
          viewingSlotId,
          searchFilters,
          sellerActionData,
        });

        if (!result.success) {
          throw new Error(result.error || 'Ett fel uppstod');
        }

        // Identify user for tracking
        if (result.user) {
          identify(result.user);
        }

        // Refresh user data to update UI state (e.g., wishlist items, interests)
        // Don't await - let SWR revalidate in the background to avoid blocking the UI
        mutate(['users', user?.id]);
        
        // Revalidate buyer dashboard server cache
        revalidateBuyerDashboard();

        // Store the search ID if a search profile was just created
        // The searchId is nested in actionResult from the backend response
        const searchId = result.response?.searchId || result.response?.actionResult?.searchId;
        if (action === 'created_search_profile' && searchId) {
          useUIStore.getState().setLastCreatedSearchId(searchId);
        }

        // Open the standalone confirmation modal unless caller wants to handle it
        if (!skipConfirmationModal) {
          const currentModals = useUIStore.getState().modals;
          setModals({
            ...currentModals,
            actionConfirmationOpen: {
              isOpen: true,
              home: home || null,
              project: project || null,
              action,
              email: user.email,
            },
          });
        }

        return { success: true, response: result.response };
      } catch (error) {
        console.error('Error performing action:', error);
        const errorMessage = error instanceof Error ? error.message : 'Ett fel uppstod';
        
        // On error, fall back to showing the login form (user can retry)
        const errorModals = useUIStore.getState().modals;
        setModals({
          ...errorModals,
          enhancedLoginOpen: {
            isOpen: true,
            home,
            project,
            action,
            redirectTo,
          },
        });

        return { success: false, error: errorMessage };
      } finally {
        setIsLoading(false);
      }
    } else {
      // User is not logged in OR doesn't have complete profile
      // Open the enhanced login modal - it will handle showing the right step
      const loginModals = useUIStore.getState().modals;
      setModals({
        ...loginModals,
        enhancedLoginOpen: {
          isOpen: true,
          home,
          project,
          action,
          redirectTo,
          searchFilters,
        },
      });

      // Return needsAuth so caller knows user needs to complete login
      return { success: true, needsAuth: true };
    }
  }, [
    user,
    agency?.id,
    setModals,
    setLoginViaViewingSlotId,
    identify,
  ]);

  return {
    executeAction,
    isLoading,
    canExecuteImmediately,
  };
};
